<template>
  <div class="d-inline-block">

    <slot name="open-button" v-bind:showModal="showModal">
      <!--
        Use the following slot to replace the show modal button
        <template #open-button="buttonProps">
          <button @click="buttonProps.showModal">Add</button>
        </template>
      -->
      <button @click="showModal" :disabled="disabled" class="btn btn-primary mx-2">
        Open
      </button>
    </slot>

    <b-modal-custom
      v-model="modalDisplay"
      :modal-class.sync="$options.data.themeClass"
      @hidden="handleClose"
      @shown="handleShown"
      size="md"
      ok-only
      ok-title="Close">

      <template #modal-header>
        <h5>{{ modalTitle }}</h5>
        <b-button-close @click="handleClose">&times;</b-button-close>
      </template>

      <vue-perfect-scrollbar class="filter-presets">
        <div v-if="!computedPresets.length" class="no-items">
          There are no presets to display
        </div>

        <template v-for="(section, sectionIndex) in computedPresets">
          <div :key="`section-${sectionIndex}`" class="preset-group">
            <h5 class="preset-group__title">{{ section.sectionName }}</h5>

            <div v-for="(preset, presetIndex) in section.presets"
                 :key="`preset-${sectionIndex}-${presetIndex}`"
                 class="preset">

              <div class="link">
                <div @click="applyPreset(preset)">
                  <span class="link__title">{{ preset.text }}</span>
                </div>
              </div>

              <div class="actions">
                <b-button variant="link"
                          title="Remove the current preference"
                          class="action-button remove-button"
                          @click="removePresetHandler(preset)"
                          :disabled="apiRequestIsPerforming">
                  <i class="fa fa-lg fa-remove"></i>
                </b-button>
              </div>

            </div>
          </div>
        </template>
      </vue-perfect-scrollbar>

    </b-modal-custom>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import sortBy from 'lodash-es/sortBy'
import startCase from 'lodash-es/startCase'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import debounce from 'lodash-es/debounce'
import BModalCustom from '@/base_components/BModalCustom'

// constants
const debounceDelay = 50;


export default {
  name: 'filter-presets-modal',

  components: {
    BModalCustom,
    'vue-perfect-scrollbar': VuePerfectScrollbar,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    modalTitle: {
      type: String,
      default: 'Details',
    },
    textFieldName: {
      type: String,
      default: 'Name',
    },
    textFieldValue: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      modalDisplay: false,
      apiRequestIsPerforming: false,
    }
  },

  methods: {
    ...mapMutations('api/presets', [
      'setActivePreset',
    ]),

    ...mapMutations('api/presets', [
      'updateIsPresetMatchFilters',
      'setStateValue',
    ]),

    ...mapActions('api/presets', [
      'loadAllPresets',
      'saveCurrentFilterSettings',
      'removePreset',
    ]),

    showModal() {
      this.modalDisplay = true;
    },

    async handleShown() {
      await this.loadPreferences();
    },

    async loadPreferences() {
      await this.loadAllPresets();
    },

    async handleClose(evt) {
      if(evt) {
        evt.preventDefault();
      }

      this.modalDisplay = false;
      this.$emit('closed');
    },

    applyPreset(preset) {
      if(preset && preset.url !== this.$route.path) {
        this.setActivePreset(preset.value);
        this.setStateValue({name: 'lockPresetsSelector', value: true});
        this.$router.push(preset.url).then(() => {
          this.setStateValue({name: 'lockPresetsSelector', value: false});
        });
      } else {
        this.setActivePreset(preset.value);
      }

      this.handleClose();
    },

    async removePresetHandler(preset) {
      const modalResult = await this.$bvModal.msgBoxConfirm(
        `Are you sure to remove the "${preset.text}" ?`,
        {
          title: 'Preference removing confirmation',
          okVariant: 'danger',
          okTitle: 'YES',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: false,
        }
      );

      if (modalResult) {
        await this.removePreset(preset.value);
        await this.loadPreferences();
      }
    },


  },

  computed: {
    ...mapGetters(
      'api', [
        'getActiveRequestsCount',
      ]
    ),

    ...mapGetters(
      'api/presets', [
        'getAllPresets',
        'getActivePreset',
        'isPresetMatchFilters',
        'getLockPresetsSelector',
      ]
    ),

    computedPresets() {
      const sortedPresets = sortBy(
        this.getAllPresets, ['viewName', 'label']
      );

      let presets = [];
      let currentGroup = {};

      sortedPresets.forEach(preset => {
        let presetChoice = {
          url: preset.viewName,
          value: preset.viewId,
          text: `${preset.label}`,
        };

        if(currentGroup.sectionName !== startCase(preset.viewName)) {
          currentGroup = {
            sectionName: startCase(preset.viewName),
            presets: [presetChoice],
          }
          presets.push(currentGroup);
        } else {
          currentGroup.presets.push(presetChoice);
        }
      });

      return presets;
    },
  },

  watch: {
    getActiveRequestsCount: debounce(function (newValue) {
      this.apiRequestIsPerforming = !!newValue;
    }, debounceDelay),
  },
}
</script>
